"use client";

import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import type {
  ITradingCompetitionColumns,
  ITradingCompetitionYou,
} from "@/sanity/pages/PageTradingCompetitionTemplate";
import { METAL } from "./PodiumCard";
import Ticker from "./Ticker";
import { formatDelta, formatPoints, LiveTrader, LiveYou, rankLabel } from "./useLiveBoard";

// [line-height:normal], not leading-[normal]: tailwind-merge drops leading-* when a text-* size follows in cn()
const TEXT = "font-geist [line-height:normal] whitespace-nowrap";
const ROW = "flex items-center gap-[1rem] px-[1.5rem] lg:gap-[2rem] lg:px-[3rem]";
const COL_RANK = "w-[6rem] shrink-0 lg:w-[11rem]";
const COL_POINTS = "w-[8rem] shrink-0 text-right lg:w-[20rem]";
const COL_LAST = "hidden w-[17.5rem] shrink-0 text-right lg:block";
const COL_PRIZE = "w-[9rem] shrink-0 text-right lg:w-[17.5rem]";

function YouRow({ you, live }: { you?: ITradingCompetitionYou; live: LiveYou }) {
  return (
    <div className={cn(ROW, "h-[8rem] w-full shrink-0 overflow-hidden rounded-[0.5rem] border border-solid border-white/[0.14] bg-white/5")}>
      <div className="flex shrink-0 rounded-full bg-white/[0.08] px-[1.5rem] py-[0.75rem]">
        <Ticker value={String(live.rank)} className={cn(TEXT, "text-[1.75rem] font-medium text-[#f2f7f5]")} />
      </div>
      <p className={cn(TEXT, "mb-0 text-[2rem] font-medium text-[#f2f7f5]")}>{you?.name}</p>
      {you?.badge && (
        <div className="flex shrink-0 rounded-[0.25rem] border border-solid border-white/10 bg-white/5 px-[1rem] py-[0.25rem]">
          <p className={cn(TEXT, "mb-0 text-[1.75rem] font-semibold text-[#f2f7f5]")}>{you.badge}</p>
        </div>
      )}
      <i aria-hidden="true" className="min-w-0 flex-1" />
      <div className="flex shrink-0 items-end gap-[0.5rem]">
        <Ticker value={formatPoints(live.points)} className={cn(TEXT, "text-[2.25rem] font-medium text-[#f2f7f5]")} />
        <p className={cn(TEXT, "mb-0 text-[1.75rem] font-normal text-[rgba(242,247,245,0.5)]")}>{you?.pointsUnit}</p>
      </div>
      <i aria-hidden="true" className="min-w-0 flex-1 lg:hidden" />
      <Ticker
        value={formatDelta(live.lastDay)}
        className={cn(TEXT, "hidden text-[1.75rem] font-medium text-[#f2f7f5] lg:inline-grid")}
      />
      <p className={cn(TEXT, "mb-0 text-[2rem] font-semibold text-[#f2f7f5]")}>{you?.prize}</p>
    </div>
  );
}

export default function LeaderboardTable({
  rows,
  prizes,
  you,
  live,
  columns,
  note,
}: {
  rows: LiveTrader[];
  /** Prize per position (the prize belongs to the place, not to the trader). */
  prizes: (string | undefined)[];
  you?: ITradingCompetitionYou;
  live: LiveYou;
  columns?: ITradingCompetitionColumns;
  note?: string;
}) {
  return (
    <>
      <YouRow you={you} live={live} />

      <div className="flex h-[29.625rem] w-full shrink-0 flex-col overflow-hidden rounded-[0.5rem] border border-solid border-[rgba(214,238,231,0.1)] bg-[rgba(214,238,231,0.02)] lg:h-[49.75rem]">
        <div
          className={cn(
            ROW,
            TEXT,
            "h-[6rem] shrink-0 bg-[rgba(214,238,231,0.03)] text-[1.75rem] font-medium tracking-[0.105rem] text-[rgba(242,247,245,0.42)]",
          )}>
          <p className={cn("mb-0", COL_RANK)}>{columns?.rank}</p>
          <p className="mb-0 min-w-0 flex-1">{columns?.trader}</p>
          <p className={cn("mb-0", COL_POINTS)}>{columns?.points}</p>
          <p className={cn("mb-0", COL_LAST)}>{columns?.lastDay}</p>
          <p className={cn("mb-0", COL_PRIZE)}>{columns?.prize}</p>
        </div>

        {rows.map((t, i) => {
          const place = i + 1;
          const metal = place <= 3 ? METAL[place as 1 | 2 | 3] : undefined;
          return (
            <motion.div
              key={t.id}
              layout="position"
              transition={{ layout: { duration: 0.6, ease: [0.22, 0.61, 0.36, 1] } }}
              className={cn(ROW, "relative h-[7.5rem] w-full shrink-0 border-t border-solid border-[rgba(214,238,231,0.1)]")}>
              <div className={cn("flex items-center", COL_RANK)}>
                <div
                  className={cn(
                    "flex h-[4.5rem] w-[5.5rem] items-center justify-center rounded-full lg:w-[8rem]",
                    metal ? "border border-solid bg-white/[0.06]" : "bg-[rgba(214,238,231,0.06)]",
                  )}
                  style={metal ? { borderColor: `rgba(${metal},0.5)` } : undefined}>
                  <p
                    className={cn(TEXT, "mb-0 text-[1.75rem] font-medium", !metal && "text-[rgba(214,238,231,0.66)]")}
                    style={metal ? { color: `rgb(${metal})` } : undefined}>
                    {rankLabel(place)}
                  </p>
                </div>
              </div>
              <div className="flex min-w-0 flex-1 items-center gap-[1.5rem] overflow-hidden">
                <div className="flex h-[4rem] w-[4rem] shrink-0 items-center justify-center rounded-full bg-[rgba(214,238,231,0.07)]">
                  <p className="mb-0 font-blacker text-[1.75rem] leading-[normal] font-normal text-[rgba(214,238,231,0.66)] italic lg:text-[2.25rem]">
                    {t.name.charAt(0)}
                  </p>
                </div>
                <p className={cn(TEXT, "mb-0 text-[1.75rem] font-medium text-[#f2f7f5] lg:text-[2rem]")}>{t.name}</p>
              </div>
              <p className={cn(TEXT, COL_POINTS, "mb-0 text-[1.75rem] font-medium text-[#f2f7f5] lg:text-[2rem]")}>
                <Ticker value={formatPoints(t.points)} />
              </p>
              <p
                className={cn(
                  TEXT,
                  COL_LAST,
                  "mb-0 text-[1.75rem] font-medium",
                  t.lastDay < 0 ? "text-[#ffb36b]" : "text-[#12f0b4]",
                )}>
                <Ticker value={formatDelta(t.lastDay)} />
              </p>
              <p
                className={cn(TEXT, COL_PRIZE, "mb-0 text-[1.75rem] font-semibold lg:text-[2rem]", !metal && "text-[#f2f7f5]")}
                style={metal ? { color: `rgb(${metal})` } : undefined}>
                {prizes[i]}
              </p>
              {metal && (
                <i
                  aria-hidden="true"
                  className="absolute top-[-0.125rem] left-0 h-[7.5rem] w-[0.5rem]"
                  style={{ background: `rgb(${metal})` }}
                />
              )}
            </motion.div>
          );
        })}
      </div>

      {note && (
        <p className="mb-0 w-full text-center font-geist text-[1.75rem] leading-[normal] font-normal text-[rgba(242,247,245,0.42)] lg:w-auto lg:whitespace-nowrap">
          {note}
        </p>
      )}
    </>
  );
}
